import { FC, useState, useContext, useMemo, useEffect } from 'react';
import Spacer from '@components/Spacer';
import TextInput from '@components/FormFields/TextInput';
import Button from '@components/DesignLibrary/Button';
import { AiAgent, AiAgentModels } from '@/typings/questionnaires-api-models';
import TeamsContext from '@/providers/Teams/TeamsContext';
import useNoodleApi from '@/hooks/useNoodleApi';
import getDataReferences from '@/tsClient/questionnaires/getDataReferences';
import isNonMasterLinkedList from '@/helpers/questionnaires/isNonMasterLinkedList';
import { Select, TextArea } from '../DesignLibrary/Atoms';
import CollapsibleArea from '../CollapsibleArea';
import s from './CreateEditAiAgentForm.module.scss';
import Buttons from '../Buttons';
import PlusCircle from '../Icons/PlusCircle';
import TrashSimple from '../Icons/TrashSimple';
import { useFormEditorContext } from '../Questionnaire/questionnaireEditor/FormEditor/FormEditorContext';

type AiAgentCreationData = {
  id?: string;
  creatorId: string;
  questionnaireTemplateId: string;
  title: string;
  aiModel: AiAgentModels;
  instructions: string | null;
  fieldData?: string[];
  fieldOutputs?: string[];
  externalData?: string[];
  internalData?: unknown;
};

const modelOptions: { key: AiAgentModels; label: string }[] = [
  { key: 'gpt4', label: 'GPT-4' },
  { key: 'gpt4o', label: 'GPT-4o' },
  { key: 'claude37', label: 'Claude 3.7' },
  { key: 'claude35sonnet', label: 'Claude 3.5' },
  { key: 'claude2', label: 'Claude 2'},
  { key: 'gpt35turbo', label: 'GPT-3.5-Turbo'},
  { key: 'o3mini', label: 'o3 Mini' },
  { key: 'googlePlaces', label: 'Google Places' },
  { key: 'boxIndex', label: 'Box Index Expression'},
  { key: 'omitForPdfFills', label: 'Omit for PDF fills'},
];

type Props = {
  aiAgent: AiAgent | null;
  questionnaireTemplateId: string;
  isSubmitting: boolean;
  onSubmit: (data: AiAgentCreationData) => void;
  onDelete: () => void;
};

const CreateEditAiAgentForm: FC<Props> = ({
  aiAgent,
  questionnaireTemplateId,
  isSubmitting,
  onSubmit,
  onDelete,
}) => {
  const { creatorId } = useContext(TeamsContext);
  const { formData, fieldsById } = useFormEditorContext();
  const [aiModel, setAiModel] = useState<AiAgentModels | null>(aiAgent?.aiModel ?? null);
  const [instructions, setInstructions] = useState<string | null>(aiAgent?.instructions ?? null);
  const [fieldData, setFieldData] = useState(aiAgent?.fieldData ?? []);
  const [externalData, setExternalData] = useState(aiAgent?.externalData ?? []);
  const [internalData, setInternalData] = useState(() =>
    aiAgent?.internalData ? Object.entries(aiAgent.internalData).map(([key, label]) => ({ key, label })) : [],
  );

  const [title, setTitle] = useState<string | null>(aiAgent?.title ?? null);
  const [fieldOutputs, setFieldOutputs] = useState<string[]>(aiAgent?.fieldOutputs ?? []);

  const { data: dataReferences, getData: getDataReferencesFn } = useNoodleApi(getDataReferences);

  const availableInputFields = useMemo(() => {
    const opts = formData.sections.flatMap((section) =>
      section.fields
        .filter((f) => {
          if (f.listFieldTemplateId) {
            const listField = fieldsById[f.listFieldTemplateId];
            const isLinkedListChild = isNonMasterLinkedList(listField);
            if (isLinkedListChild) {
              return false;
            }
            return true;
          }
          return true;
        })
        .map((f) => ({
          description: section.label,
          key: f.key,
          label: f.label,
        })),
    );

    return opts;
  }, [formData, fieldsById]);

  const availableOutputFields = useMemo(() => {
    const initialOutputKeys = aiAgent?.fieldOutputs ?? [];
    const opts = formData.sections.flatMap((section) =>
      section.fields
        .filter((f) => !(f.autofill?.action === 'ai-agent') || initialOutputKeys.includes(f.key))
        .filter((f) => {
          if (f.listFieldTemplateId) {
            const listField = fieldsById[f.listFieldTemplateId];
            const isLinkedListChild = isNonMasterLinkedList(listField);
            if (isLinkedListChild) {
              return false;
            }
            return true;
          }
          return true;
        })
        .map((f) => ({
          description: section.label,
          key: f.key,
          label: f.label,
        })),
    );

    return opts;
  }, [formData, fieldsById, aiAgent?.fieldOutputs]);

  const availableExternalData = useMemo(() => {
    const opts = dataReferences?.items.map((ref) => ({
      description: `Version ${ref.version} - ${ref.versionLabel}`,
      key: ref.slug,
      label: ref.label,
    }));

    return opts ?? [];
  }, [dataReferences]);

  useEffect(() => {
    const getExternalData = async (): Promise<void> => {
      await getDataReferencesFn({
        page: 1,
        perPage: 100_000,
      });
    };
    getExternalData();
  }, [getDataReferencesFn]);

  const fieldLabelsMap: Record<string, string> = availableInputFields.reduce((acc, field) => {
    acc[field.key] = field.label;
    return acc;
  }, {} as Record<string, string>);

  const externalDataMap: Record<string, string> = availableExternalData.reduce((acc, dataSet) => {
    acc[dataSet.key] = dataSet.label;
    return acc;
  }, {} as Record<string, string>);

  const handleAddFieldInput = (): void => {
    setFieldData([...fieldData, '']);
  };

  const handleAddFieldOutput = (): void => {
    setFieldOutputs([...fieldOutputs, '']);
  };

  const handleAddExternalDataSet = (): void => {
    setExternalData([...externalData, '']);
  };

  const handleAddInternalDataSet = (): void => {
    setInternalData([...internalData, { key: '', label: '' }]);
  };

  const handleDeleteFieldInput = (indexToDelete: number): void => {
    setFieldData(fieldData.filter((_, index) => index !== indexToDelete));
  };

  const handleDeleteFieldOutput = (indexToDelete: number): void => {
    setFieldOutputs(fieldOutputs.filter((_, index) => index !== indexToDelete));
  };

  const handleDeleteExternalDataSet = (indexToDelete: number): void => {
    setExternalData(externalData.filter((_, index) => index !== indexToDelete));
  };

  const handleDeleteInternalDataSet = (indexToDelete: number): void => {
    setInternalData(internalData.filter((_, index) => index !== indexToDelete));
  };

  const setDataItem = ({
    key,
    label,
    index,
    itemType,
  }: {
    key: string;
    label: string;
    index: number;
    itemType: 'input' | 'output' | 'external' | 'internal';
  }): void => {
    if (itemType === 'input') {
      setFieldData((prevFieldData) =>
        prevFieldData.map((oldKey, i) =>
          i === index ? key : oldKey,
        ),
      );
    }
    if (itemType === 'output') {
      setFieldOutputs((prevFieldOutputs) =>
        prevFieldOutputs.map((item, i) =>
          i === index ? key : item,
        ),
      );
    }

    if (itemType === 'external') {
      setExternalData((prevExternalData) =>
        prevExternalData.map((item, i) =>
          i === index ? key : item,
        ),
      );
    }

    if (itemType === 'internal') {
      setInternalData((prevInternalData) =>
        prevInternalData.map((item, i) =>
          i === index ? { key, label } : item,
        ),
      );
    }
  };

  const handleSubmit = (): void => {
    if (!title || !aiModel) {
      return;
    }
    if (creatorId) {
      const aiAgentCreatorData = {
        aiModel,
        creatorId,
        externalData,
        fieldData,
        fieldOutputs,
        id: aiAgent?.id ?? undefined,
        instructions,
        internalData: internalData.reduce(
          (acc, field) => ({ ...acc, [field.key]: field.label }),
          {},
        ),
        questionnaireTemplateId,
        title,
      };
      onSubmit(aiAgentCreatorData);
    }
  };

  return (
    <div className={s.wrapper}>
      <div className={s.section}>
        <div className={s.content}>
          <TextInput
            id="title"
            value={title ?? ''}
            onChange={setTitle}
            label={'Agent Title'}
            placeholder={`Enter title`}
            required
          />
        </div>
      </div>
      <CollapsibleArea title={`Field Inputs (${fieldData.length})`}>
        <div className={s.content}>
          {fieldData.map((field, index) => (
            <div key={index}>
              <div className={s.dataRow}>
                <Select
                  label=""
                  options={availableInputFields}
                  value={field}
                  onChange={(key) => setDataItem({ index, itemType: 'input', key, label: fieldLabelsMap[key] })}
                />
                <Buttons isExtraSmall isWrapper className={s.trashInput} onClick={() => handleDeleteFieldInput(index)}>
                  <>
                    <TrashSimple size={8} weight="fill" />
                  </>
                </Buttons>
              </div>
              <Spacer size={8}/>
            </div>
          ))}
          <Spacer size={8}/>
          <Buttons isExtraSmall isWrapper className={s.addInput} onClick={handleAddFieldInput}>
            <>
              <PlusCircle size={8} weight="fill" />
              Add field input
            </>
          </Buttons>
        </div>
      </CollapsibleArea>
      <CollapsibleArea title={`External Data Inputs (${externalData.length})`}>
        <div className={s.content}>
          {externalData.map((field, index) => (
            <div key={index}>
              <div className={s.dataRow}>
                <Select
                  label=""
                  options={availableExternalData}
                  value={field}
                  onChange={(slug) => setDataItem({ index, itemType: 'external', key: slug, label: externalDataMap[slug] })}
                />
                <Buttons isExtraSmall isWrapper className={s.trashInput} onClick={() => handleDeleteExternalDataSet(index)}>
                  <>
                    <TrashSimple size={8} weight="fill" />
                  </>
                </Buttons>
              </div>
              <Spacer size={8}/>
            </div>
          ))}
          <Spacer size={8}/>
          <Buttons isExtraSmall isWrapper className={s.addInput} onClick={handleAddExternalDataSet}>
            <>
              <PlusCircle size={8} weight="fill" />
              Add external data input
            </>
          </Buttons>
        </div>
      </CollapsibleArea>
      <CollapsibleArea title={`Internal Data Inputs (${internalData.length})`}>
        <div className={s.content}>
          {internalData.map((field, index) => (
            <div key={index}>
              <div className={s.dataRow}>
                <Select
                  label=""
                  options={[
                    { key: 'income-data', label: 'Income Data' },
                    { key: 'asset-data', label: 'Asset Data' },
                    { key: 'personal-data', label: 'Personal Data' },
                  ]}
                  value={field.key}
                  onChange={(id) => {
                    const selectedOption = [
                      { key: 'income-data', label: 'Income Data' },
                      { key: 'asset-data', label: 'Asset Data' },
                      { key: 'personal-data', label: 'Personal Data' },
                    ].find(option => option.key === id);
                    setDataItem({ index, itemType: 'internal', key: id, label: selectedOption?.label ?? '' });
                  }}
                />
                <Buttons isExtraSmall isWrapper className={s.trashInput} onClick={() => handleDeleteInternalDataSet(index)}>
                  <>
                    <TrashSimple size={8} weight="fill" />
                  </>
                </Buttons>
              </div>
              <Spacer size={8}/>
            </div>
          ))}
          <Spacer size={8}/>
          <Buttons isExtraSmall isWrapper className={s.addInput} onClick={handleAddInternalDataSet}>
            <>
              <PlusCircle size={8} weight="fill" />
              Add internal data input
            </>
          </Buttons>
        </div>
      </CollapsibleArea>
      <div className={s.section}>
        <div className={s.content}>
          <TextArea optional label='' description='Additional Instructions for Agent' onChange={setInstructions} value={instructions} />
        </div>
      </div>
      <div className={s.section}>
        <div className={s.content}>
          <Select
            label=""
            description="Ai model"
            options={modelOptions}
            value={aiModel}
            onChange={(v) => {
              const aiAgentModel = v as AiAgentModels;
              setAiModel(aiAgentModel);
            }}
            optional
          />
        </div>
      </div>
      <CollapsibleArea title={`Field Outputs (${fieldOutputs.length})`}>
        <div className={s.content}>
          {fieldOutputs.map((field, index) => (
            <div key={index}>
              <div className={s.dataRow}>
                <Select
                  label=""
                  options={availableOutputFields}
                  value={field}
                  onChange={(key) => setDataItem({ index, itemType: 'output', key, label: fieldLabelsMap[key] })}
                />
                <Buttons isExtraSmall isWrapper className={s.trashInput} onClick={() => handleDeleteFieldOutput(index)}>
                  <>
                    <TrashSimple size={8} weight="fill" />
                  </>
                </Buttons>
              </div>
              <Spacer size={8}/>
            </div>
          ))}
          <Spacer size={8}/>
          <Buttons isExtraSmall isWrapper className={s.addInput} onClick={handleAddFieldOutput}>
            <>
              <PlusCircle size={8} weight="fill" />
              Add field output
            </>
          </Buttons>
        </div>
      </CollapsibleArea>
      <div className={s.button}>
        <Button
          onClick={handleSubmit}
          loading={isSubmitting}
          variant='primary'
          fullWidth
          size='md'
        >
          Save
        </Button>
        {aiAgent && (
          <Button fullWidth variant="destructive" size="md" onClick={onDelete} disabled={isSubmitting}>
            Delete AI Agent
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreateEditAiAgentForm;
